import React, { useState, useRef, useEffect } from "react";
import "../../theme/tailwind.css";
import * as StringUtils from "@utils/stringUtils";
import { IProgressCheckDescription } from "@interfaces/Components";
import classNames from "classnames";

const MAX_SUBTITLE_HEIGHT_BOTTOM_PADDING = 4;
const THREE_LINE_HEIGHT_THRESHOLD = 4.5; // Approximately 3 lines in em units

const ProgressCheckDescription: React.FC<IProgressCheckDescription> = (props) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const measurementRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [shouldShowCollapsible, setShouldShowCollapsible] = useState<boolean>(true);

  // Measure content height on mount to determine if collapsible should be shown
  useEffect(() => {
    if (props.description && measurementRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (measurementRef.current) {
          const measuredHeight = measurementRef.current.scrollHeight;
          // Convert 4.5em threshold to pixels using computed line-height
          const computedStyle = window.getComputedStyle(measurementRef.current);
          const fontSize = parseFloat(computedStyle.fontSize) || 13;
          const lineHeight = parseFloat(computedStyle.lineHeight) || fontSize * 1.54; // Using 1.54 from existing leading
          const thresholdPixels = THREE_LINE_HEIGHT_THRESHOLD * lineHeight;

          // If content height is <= 3 lines, hide collapsible
          setShouldShowCollapsible(measuredHeight > thresholdPixels);
        }
      });
    } else if (!props.description) {
      setShouldShowCollapsible(false);
    }
  }, [props.description]);

  // Measure content height when expanded or when content changes
  useEffect(() => {
    if (isExpanded && contentRef.current) {
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        if (contentRef.current) {
          // Measure the full height when expanded (line-clamp-3 is removed)
          setContentHeight(contentRef.current.scrollHeight + MAX_SUBTITLE_HEIGHT_BOTTOM_PADDING);
        }
      });
    }
    // Don't reset height when collapsed - keep it for smooth collapse animation
    // Only re-measure when description content changes
  }, [isExpanded, props.description]);

  return (
    <>
      {/* Hidden measurement element to determine if content exceeds 3 lines */}
      <div
        ref={measurementRef}
        className="absolute opacity-0 pointer-events-none invisible"
        style={{ position: "absolute", top: "-9999px" }}
      >
        <div
          className="text-acc-13px leading[1.54] tracking-[-0.1px] text-grey-70 preceptor-description mb-1 whitespace-pre-wrap"
          dangerouslySetInnerHTML={StringUtils.sanitizeHTML(props.description)}
        />
      </div>

      {shouldShowCollapsible ? (
        <div className="mb-2">
          <div
            className="overflow-hidden transition-all duration-[250ms] ease-in-out"
            style={{
              maxHeight: isExpanded
                ? contentHeight
                  ? `${contentHeight}px`
                  : "1000px" // Fallback while measuring
                : "4.5em", // Collapsed state
            }}
          >
            <div
              ref={contentRef}
              className={classNames(
                "text-acc-13px leading[1.54] tracking-[-0.1px] text-grey-70 preceptor-description mb-1 whitespace-pre-wrap",
                {
                  "line-clamp-3": !isExpanded,
                }
              )}
              dangerouslySetInnerHTML={StringUtils.sanitizeHTML(props.description)}
            />
          </div>
          <div className="flex justify-start mt-2">
            <button
              type="button"
              className="text-[13px] font-semibold tracking-[-0.05px] text-cta-blue acc-underline mt-1 cursor-pointer self-end bg-transparent border-none p-0"
              onClick={() => setIsExpanded(!isExpanded)}
            >
              {isExpanded ? "Show less" : "Show more"}
            </button>
          </div>
        </div>
      ) : (
        // Render normally without collapsible functionality for short text
        <div
          className="text-acc-13px leading[1.54] tracking-[-0.1px] text-grey-70 preceptor-description mb-1 whitespace-pre-wrap"
          dangerouslySetInnerHTML={StringUtils.sanitizeHTML(props.description)}
        />
      )}
    </>
  );
};

export default ProgressCheckDescription;
